export interface SecurityHeaders {
  authorization: string;
  clientType: string;
  timestampSlice: string;
  secret: string;
}

const encoder = new TextEncoder();

/**
 * - Nếu đã có access token / Authorization thì giữ nguyên Authorization đó.
 * - Nếu chưa có token thì sinh Bearer JWT ngắn hạn ký bằng secret động theo phút.
 */
export async function generateRequestHeaders(
  clientType: string,
  secretConfig: string,
  authorization?: string | null,
): Promise<SecurityHeaders> {
  const timestampSlice = String(Math.floor(Date.now() / 60000));
  const baseSecret = `${clientType}${secretConfig}`;

  const secretBytes = await hmacSha256(
    toBytes(timestampSlice),
    toBytes(baseSecret),
  );
  const secret = toBase64(secretBytes);

  if (authorization) {
    return {
      authorization,
      clientType,
      timestampSlice,
      secret,
    };
  }

  const nowSec = Math.floor(Date.now() / 1000);
  const header = toBase64Url(
    toBytes(JSON.stringify({ alg: 'HS256', typ: 'JWT' })),
  );
  const payload = toBase64Url(
    toBytes(
      JSON.stringify({
        timestamp: nowSec,
        iat: nowSec,
        exp: nowSec + 30 * 60,
      }),
    ),
  );

  const signingInput = `${header}.${payload}`;
  const signature = await hmacSha256(toBytes(signingInput), secretBytes);

  return {
    authorization: `Bearer ${signingInput}.${toBase64Url(signature)}`,
    clientType,
    timestampSlice,
    secret,
  };
}

function toBytes(value: string): Uint8Array {
  return encoder.encode(value);
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

async function hmacSha256(
  message: Uint8Array,
  key: Uint8Array,
): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    toArrayBuffer(message),
  );
  return new Uint8Array(signature);
}

function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function toBase64Url(bytes: Uint8Array): string {
  return toBase64(bytes)
    .replace(/=+$/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
}
